// ---------------------------------------------------------------------------
// Adapter — converte o snapshot do backend (handoff M1+M5 §2.1) no shape
// consumido pelos componentes Hi-Fi (HFRoutePanel, StopDrawer, HFUnroutedDock,
// HFMap). Mantém a UI estável enquanto o contrato real do backend evolui.
//
// Snapshot recebido (shape backend M1+M5):
//   {
//     schedule: { id, schedule_date, schedule_period, schedule_status,
//                 last_optimized_at, updated_at, ... },
//     summary:  { total_items, total_routed, total_unrouted, total_order_value },
//     technicians: [ { id, full_name, department, is_assigned } ],  ← universo elegível
//     subclusters: [ { id, subcluster_label, subcluster_sequence,
//                      assigned_technician|null, vehicle|null,
//                      volumetry_limit_value, travel_time_limit_minutes,
//                      service_time_limit_minutes, items[], summary } ],
//     unrouted:    [ <item>, ... ],
//   }
//
// Cada item carrega: id, order_number, customer_name, service_address,
// address_latitude, address_longitude, route_sequence, estimated_arrival_time,
// estimated_duration_minutes, customer_window_start/end, item_status,
// active_events[], services[] (seleção do catálogo unificado) / etc.
//
// Shape devolvido (consumido pelo RouteManagement/index.jsx):
//   {
//     schedule, summary,
//     subclusters: [ { id, subclusterId, label, sequence, technician|null,
//                      vehicle|null, limits, c, status, statusLbl, summary,
//                      itemsCount } ],
//     stopsBySubcluster: { '<subclusterId>': [ <stop>, ... ] },
//     technicians: [ { employeeId, name, init, c, department, isAssigned } ],
//     unrouted:    [ <stop>, ... ],
//   }
//
// O id do card aqui é uma STRING derivada do subcluster.id (ex: '12') —
// necessário porque o estado do RouteManagement chaveia stopsBySubcluster por
// string e o HFRoutePanel também. subclusterId numérico fica disponível como
// prop adicional pra chamadas API (group-items, assign-technician, CRUD).
// ---------------------------------------------------------------------------

import {
   deriveSvc,
   deriveStopStatus,
   deriveSla,
   deriveDuration,
   deriveColorClass,
   deriveInitials,
   deriveTechStatus,
   derivePresence,
   deriveOtw,
   deriveTimeline,
   routeColorByIndex,
} from './derivations';
import { totalServiceQty } from './serviceFields';

// ─── Stop (uma parada na rota ou no dock) ──────────────────────────────────
// Mantém shape compatível com o mock atual do StopDrawer/HFRoutePanel.
// O campo `raw` carrega o item original — útil pro drawer renderizar campos
// que ainda não tem helper dedicado (lat/lng, customer_phone, etc.).

export function adaptItemToStop(item) {
   const events = item.active_events || [];
   const eta = item.estimated_arrival_time
      ? String(item.estimated_arrival_time).slice(0, 5)
      : '—';
   const winStart = item.customer_window_start
      ? String(item.customer_window_start).slice(0, 5)
      : null;
   const winEnd = item.customer_window_end
      ? String(item.customer_window_end).slice(0, 5)
      : null;
   // Seleção de serviços (handoff 0039): array N:N do catálogo unificado. Cada
   // entrada { commission_table_id, service_code, service_description, quantity }.
   // services.length≥1 = item classificado (tem ≥1 serviço). Item PENDENTE sem
   // serviço barra a otimização (422 service_classification_required).
   const services = Array.isArray(item.services) ? item.services : [];
   const serviceTotal = totalServiceQty(services);
   return {
      // Compatibilidade com o shape mock do protótipo (HF_*_STOPS).
      seq:       item.route_sequence ?? null,
      id:        item.order_number || `ID-${item.id}`,
      nm:        item.customer_name || '—',
      addr:      item.service_address || '—',
      svc:       deriveSvc(item),
      dur:       item.estimated_duration_minutes ?? deriveDuration(item),
      eta,
      status:    deriveStopStatus(item, events),
      sla:       deriveSla(item),
      presence:  derivePresence(events),
      otw:       deriveOtw(events),

      // Timeline completa (TODOS os event_kinds, em ordem cronológica) — mesma
      // fonte que o HISTÓRICO do drawer usa. O expander do card lê isto pra não
      // perder eventos intermediários (OTW múltiplos, sintéticos) que presence/
      // otw resumiam/escondiam (Ponto 4 §7).
      timeline:  deriveTimeline(events),

      // Janela de atendimento "HH:MM–HH:MM" (cap hasWindows) — usada pelo
      // card do dock e pelo drawer. null quando o operador não preencheu.
      win:       winStart ? `${winStart}–${winEnd || '…'}` : null,

      // Restrição de horário marcado (handoff 0049) — DOIS sinais distintos:
      //   - timeWindowBreach: item ROTEIRIZADO cuja ETA caiu DEPOIS do fim da
      //     janela (só via reorder manual; o otimizador nunca aloca fora).
      //     Vira o selo "fora do horário" no card da rota; o backend conta o
      //     total em summary.time_window_breaches (não recalcular no front).
      //   - optimizationSkipReason: enum/null — o otimizador RECUSOU o item, que
      //     ficou no dock. Vira a tag "Não coube no horário marcado" (etc.) no
      //     card e na ficha. É AVISO, não trava: o item pode ser arrastado
      //     manualmente mesmo assim. Roteirizado → sempre null (backend limpa).
      timeWindowBreach:      Boolean(item.time_window_breach),
      optimizationSkipReason: item.optimization_skip_reason ?? null,

      // Identidade real para chamadas API.
      itemId:    item.id,

      // Seleção de serviços (handoff 0039 §3) — array do catálogo unificado,
      // total de unidades e flag de classificado. estimatedDuration é o valor
      // derivado/gravado (Σ quantity × tempo, ou override); pode ser 0 enquanto
      // a logística não parametrizar os tempos do catálogo.
      services,
      serviceTotal,
      classified: services.length >= 1,
      estimatedDuration: item.estimated_duration_minutes ?? null,

      // Timeline planejada (handoff 0037 §B) — deslocamento até a parada e
      // espera por janela (segundos). Usados para o gap entre blocos do Gantt.
      travelSeconds: item.travel_duration_seconds ?? null,
      waitSeconds:   item.wait_duration_seconds ?? null,

      // Carga e restrições (handoff 0032) — editáveis na ficha, eixos
      // INDEPENDENTES:
      //   - cargoClass: rótulo informativo "A"|"B"|"C"|"D"|null, definido à mão
      //     pelo operador (não afeta a otimização — ADR-003). É a letra do pino
      //     no mapa e do badge no card do dock (sem classe → cinza "N/A").
      //   - carOnly: restrição dura "só carro" que entra no VRP.
      cargoClass: item.cargo_class ?? null,
      carOnly:    Boolean(item.car_only),

      // Geolocalização (handoff 0043) — coordenadas atuais (null = sem ponto)
      // e o sinal needs_geocoding. needsGeocoding=true ⇒ item PENDENTE sem
      // coordenada → barrado na otimização (guard geocode_required); dirige o
      // badge "sem localização" e a abertura do ajuste manual no mapa. Lê o
      // campo do contrato quando vier; senão deriva pela regra exata do handoff
      // (espelha o guard do backend) p/ snapshot legado sem o campo.
      lat: item.address_latitude != null ? Number(item.address_latitude) : null,
      lng: item.address_longitude != null ? Number(item.address_longitude) : null,
      needsGeocoding:
         typeof item.needs_geocoding === 'boolean'
            ? item.needs_geocoding
            : item.item_status === 'PENDENTE' &&
              (item.address_latitude == null || item.address_longitude == null),

      // Endereço atomizado (handoff 0044 / ADR-009) — 6 campos vindos do
      // customer_addresses vinculado, substituem os 2 mesclados como referência
      // no modal de ajuste de localização. customerAddressId == null ⇒ item sem
      // vínculo (manual / import sem código de cliente): os 6 vêm null e o modal
      // cai no fallback service_address/service_address_complement.
      // addressGeocodeStatus ('ok'|'pending'|'failed'|null) distingue os casos do
      // vínculo (pending = pin manual, handoff 0043); needs_geocoding segue sendo
      // a fonte de verdade do "apto a otimizar" (ADR-009 §4) — não gateia nada
      // por addressGeocodeStatus ainda.
      customerAddressId: item.customer_address_id ?? null,
      addressGeocodeStatus: item.customer_address_geocode_status ?? null,
      address: {
         street:     item.address_street ?? null,
         number:     item.address_number ?? null,
         complement: item.address_complement ?? null,
         city:       item.address_city ?? null,
         state:      item.address_state ?? null,
         zip:        item.address_zip ?? null,
      },

      // Item "travado" (M2 §2): tem ≥1 evento de status ativo. Âncora fixa —
      // não arrasta, não reordena, não muda de subcluster, não volta a
      // pendente. Usa o campo do contrato (redundante com active_events.length).
      locked:    Boolean(item.locked),

      // Sinaliza item REALIZADO sem timeline (legado v0). Usado pelo Drawer
      // pra mostrar o badge "concluído pela planilha legado, sem timeline".
      isLegacyDone:
         item.item_status === 'REALIZADO' && (!events || events.length === 0),

      // Exclusão (soft delete → CANCELADO, handoff 0048). null nos itens vivos;
      // preenchidos no balde cancelled[] — quando/quem/por quê excluiu (auditoria
      // do card "Itens excluídos"). cancelledBy = { id, nome } | null;
      // cancellationReason = { id, reason_code, description } | null (null também
      // em itens excluídos ANTES do handoff 0048 — motivo não existia).
      cancelledAt: item.cancelled_at ?? null,
      cancelledBy: item.cancelled_by_user ?? null,
      cancellationReason: item.cancellation_reason ?? null,

      // Item cru pra qualquer leitura adicional (lat/lng, telefone, etc.).
      raw: item,
   };
}

// ─── Técnico atribuído (referência embutida no subcluster) ─────────────────
// Shape leve consumido pela linha-detalhe do card e pela seção do drawer.

function adaptTechRef(tech) {
   if (!tech) return null;
   return {
      employeeId: tech.id,
      name:       tech.full_name || '—',
      init:       deriveInitials(tech.full_name),
      c:          deriveColorClass(tech.id),
      department: tech.department || null,
   };
}

// ─── Opção de técnico (universo elegível p/ o seletor de atribuição) ────────
// is_assigned: já está em algum subcluster deste schedule → desabilitar no
// seletor (regra 1-técnico-1-rota).

function adaptTechnicianOption(tech) {
   return {
      employeeId: tech.id,
      name:       tech.full_name || '—',
      init:       deriveInitials(tech.full_name),
      c:          deriveColorClass(tech.id),
      department: tech.department || null,
      isAssigned: Boolean(tech.is_assigned),
   };
}

// ─── Veículo (FK opcional no subcluster) ───────────────────────────────────

function adaptVehicle(vehicle) {
   if (!vehicle) return null;
   return {
      id:         vehicle.id,
      identifier: vehicle.vehicle_identifier || '—',
      type:       vehicle.vehicle_type || null,
   };
}

// ─── route_geometry (handoff 0030 §4) ──────────────────────────────────────
// Vem como JSON { polyline: '<encoded>', transitions: [...] }. O backend pode
// serializar como objeto OU como string JSON — normaliza para objeto. polyline
// é a polyline codificada do Google da rota inteira (board macro);
// transitions[] (trecho a trecho) é uso futuro/v2 — ignorado na v1.1.

function parseRouteGeometry(raw) {
   if (!raw) return null;
   let geom = raw;
   if (typeof raw === 'string') {
      try {
         geom = JSON.parse(raw);
      } catch {
         return null;
      }
   }
   if (!geom || typeof geom !== 'object' || !geom.polyline) return null;
   return { polyline: geom.polyline, transitions: geom.transitions || [] };
}

// ─── Subcluster (a "rota" — vira o card/coluna) ────────────────────────────
// status/statusLbl ficam vazios no início — preenchidos depois quando os
// stops já estiverem adaptados (ver adaptSnapshot).

function adaptSubcluster(sub) {
   return {
      id:           String(sub.id),
      subclusterId: sub.id,
      label:        sub.subcluster_label || `Rota ${sub.id}`,
      sequence:     sub.subcluster_sequence ?? null,
      technician:   adaptTechRef(sub.assigned_technician),
      vehicle:      adaptVehicle(sub.vehicle),
      limits: {
         volumetry:   sub.volumetry_limit_value ?? null,
         travelTime:  sub.travel_time_limit_minutes ?? null,
         serviceTime: sub.service_time_limit_minutes ?? null,
      },

      // Cor por subcluster — a rota mantém a mesma cor no mapa mesmo quando o
      // técnico muda (ou ainda não foi atribuído).
      c: deriveColorClass(sub.id),

      // Otimização (handoff 0030 §4) — campos aditivos, null quando a rota
      // ainda não foi otimizada. routeGeometry alimenta a polyline do Maps;
      // routeSequenceHash/routeOptimizedAt detectam geometria obsoleta (§5.3).
      routeGeometry:     parseRouteGeometry(sub.route_geometry),
      routeSequenceHash: sub.route_sequence_hash ?? null,
      routeOptimizedAt:  sub.route_optimized_at ?? null,

      // Máquina de estados da rota (handoff 0042 §2) — dirige a habilitação das
      // ações no board: RASCUNHO (sem técnico) → DESIGNADA (técnico na rota) →
      // ATRIBUIDA (técnico propagado aos itens; rota travada p/ mover/reordenar).
      // Default RASCUNHO p/ snapshot legado sem o campo.
      routeStatus: sub.route_status ?? 'RASCUNHO',

      // Frota + T0 (handoff 0034 §7) — campos aditivos do snapshot.
      //   vehicleClass: 'moto' | 'carro' | null (rota anônima recém-criada).
      //     Habilita a pré-checagem de feasibility no drag: item car_only NÃO
      //     pode entrar em rota 'moto' (null = permitir).
      //   routeDepartureTime: T0 da rota (TIME local "HH:MM:SS") | null.
      vehicleClass:       sub.vehicle_class ?? null,
      routeDepartureTime: sub.route_departure_time ?? null,

      // Tipo de rota (handoff 0057) — 'round_trip' (vai e volta) | 'one_way' (só
      // vai). Default 'round_trip' p/ snapshot legado sem o campo. Controla o
      // toggle de override por rota (SubclusterFormModal) e o rótulo no footer.
      routeType: sub.route_type ?? 'round_trip',
      routeTotals: {
         distanceMeters:  sub.route_total_distance_meters ?? null,
         durationSeconds: sub.route_total_duration_seconds ?? null,
         // Breakdown (handoff 0055 §2) — invariante durationSeconds = travel +
         // service + wait. null quando o subcluster não tem geometria. Mapeado
         // aditivo; a UI ainda não exibe (uso previsto: relatórios).
         travelSeconds:  sub.route_travel_duration_seconds ?? null,
         serviceSeconds: sub.route_service_duration_seconds ?? null,
         waitSeconds:    sub.route_wait_duration_seconds ?? null,
      },

      // Status agregado — recalculado em adaptSnapshot após adaptar os stops.
      status:    'idle',
      statusLbl: 'Aguardando',

      summary: sub.summary ?? { total_items: 0, total_order_value: 0 },
      itemsCount: sub.summary?.total_items ?? (sub.items || []).length,
   };
}

// ─── Snapshot completo ─────────────────────────────────────────────────────
// Devolve o shape consumido pelo index.jsx. Notar:
//   - stopsBySubcluster é chaveado por subclusterId (string), igual ao estado.
//   - Cada stop tem itemId numérico interno pra POST /events e group-items.
//   - O status agregado de cada rota é calculado depois dos stops.

export default function adaptSnapshot(snapshot) {
   const emptySummary = {
      total_items:          0,
      total_routed:         0,
      total_unrouted:       0,
      total_cancelled:      0,
      total_order_value:    0,
      // Contador de quebras de janela (handoff 0049) — itens roteirizados com
      // ETA após o fim do horário marcado. 0 quando o backend não envia (degrada
      // sem badge).
      time_window_breaches: 0,
   };

   if (!snapshot) {
      return {
         schedule: null,
         clusterOrigin: null,
         summary: emptySummary,
         subclusters: [],
         stopsBySubcluster: {},
         technicians: [],
         unrouted: [],
         cancelled: [],
      };
   }

   const stopsBySubcluster = {};
   // Enriquecimento imutável: cada rota ganha status agregado derivado dos
   // próprios stops adaptados.
   const subclusters = (snapshot.subclusters || []).map((sub, subIdx) => {
      const card = adaptSubcluster(sub);
      // route_sequence pode vir null (ordenação backend ASC NULLS LAST) —
      // fallback pra posição na lista, exatamente a sequência que o próximo
      // group-items vai persistir [1..N].
      const stops = (sub.items || []).map((item, index) => ({
         ...adaptItemToStop(item),
         seq: item.route_sequence ?? index + 1,
      }));
      stopsBySubcluster[card.id] = stops;
      const aggregate = deriveTechStatus(stops);
      return {
         ...card,
         // Cor da rota — FONTE ÚNICA, sequencial pela posição da rota (subIdx).
         // Mapa (pinos/polyline), legenda, faixa lateral do card e círculo de
         // sequência dos itens leem este mesmo valor → tudo na mesma cor.
         color:     routeColorByIndex(subIdx),
         status:    aggregate.status,
         statusLbl: aggregate.label,
         itemsCount: stops.length,
      };
   });

   const technicians = (snapshot.technicians || []).map(adaptTechnicianOption);
   const unrouted = (snapshot.unrouted || []).map(adaptItemToStop);
   // Itens excluídos (CANCELADO) — view-only (handoff 0048). Mesmo shape de stop;
   // já chegam ordenados por cancelled_at desc do backend.
   const cancelled = (snapshot.cancelled || []).map(adaptItemToStop);

   return {
      schedule: snapshot.schedule ?? null,
      // Base operacional com latitude/longitude — marcador do depósito no mapa
      // (handoff 0030 §4). Pode vir sem coordenada (nasce NULL no cadastro) —
      // o HFMap só desenha o marcador quando lat/lng existem.
      clusterOrigin: snapshot.clusterOrigin ?? null,
      summary: snapshot.summary ?? emptySummary,
      subclusters,
      stopsBySubcluster,
      technicians,
      unrouted,
      cancelled,
   };
}
